export type RealtimeVoiceFailureKind =
  | 'permission'
  | 'capability'
  | 'capacity'
  | 'timeout'
  | 'connection'
  | 'startup'
  | 'insufficient_credits';

type ErrorDetails = {
  message: string;
  name: string;
  code: string;
};

const DEFAULT_USAGE_RETRY_DELAYS_MS = [300, 1_200] as const;
const DEFAULT_RECORDING_UPLOAD_TIMEOUT_MS = 45_000;

function getNestedError(error: unknown): unknown {
  if (!error || typeof error !== 'object' || !('error' in error)) {
    return error;
  }
  const nested = error.error;
  return nested && nested !== error ? getNestedError(nested) : error;
}

function getErrorDetails(error: unknown): ErrorDetails {
  const nested = getNestedError(error);
  if (nested instanceof Error) {
    const code =
      'code' in nested && typeof nested.code === 'string' ? nested.code : '';
    return {
      message: nested.message.toLowerCase(),
      name: nested.name.toLowerCase(),
      code: code.toLowerCase(),
    };
  }
  if (typeof nested === 'string') {
    return { message: nested.toLowerCase(), name: '', code: '' };
  }
  return { message: '', name: '', code: '' };
}

export function classifyRealtimeVoiceFailure(
  error: unknown,
  microphoneAcquired = false
): RealtimeVoiceFailureKind {
  const { message, name, code } = getErrorDetails(error);
  const searchable = `${message} ${name} ${code}`;

  if (
    !microphoneAcquired &&
    (searchable.includes('notallowed') ||
      searchable.includes('permission') ||
      searchable.includes('securityerror'))
  ) {
    return 'permission';
  }
  if (
    !microphoneAcquired &&
    (searchable.includes('notfound') ||
      searchable.includes('notreadable') ||
      searchable.includes('overconstrained') ||
      searchable.includes('microphone') ||
      searchable.includes('media device'))
  ) {
    return 'capability';
  }
  if (searchable.includes('insufficient_voice_credits')) {
    return 'insufficient_credits';
  }

  if (searchable.includes('timeout') || searchable.includes('timed out')) {
    return 'timeout';
  }
  if (
    searchable.includes('connection') ||
    searchable.includes('disconnected') ||
    searchable.includes('network') ||
    searchable.includes('webrtc')
  ) {
    return 'connection';
  }
  return 'startup';
}

export function appendBoundedRealtimeItem<T>(
  queue: readonly T[],
  item: T,
  maxItems: number
): T[] {
  if (maxItems <= 0) return [];
  return [...queue, item].slice(-maxItems);
}

export function addBoundedRealtimeSetItem<T>(
  values: Set<T>,
  item: T,
  maxItems: number
): void {
  values.delete(item);
  values.add(item);
  while (values.size > Math.max(0, maxItems)) {
    const oldest = values.values().next().value;
    if (oldest === undefined) return;
    values.delete(oldest);
  }
}

export function requestRealtimeResponseTransactionally<T>(args: {
  queue: readonly T[];
  ticket: T;
  send: () => void;
  maxTickets?: number;
}): { sent: boolean; queue: T[] } {
  try {
    args.send();
  } catch {
    return { sent: false, queue: [...args.queue] };
  }

  return {
    sent: true,
    queue: appendBoundedRealtimeItem(
      args.queue,
      args.ticket,
      args.maxTickets ?? 32
    ),
  };
}

export function consumeRealtimeResponseAdmission<T, Pending extends T>(args: {
  explicitQueue: readonly T[];
  pendingUser: Pending | null;
}): {
  ticket: T | null;
  explicitQueue: T[];
  pendingUser: Pending | null;
} {
  const [explicitTicket, ...remainingExplicit] = args.explicitQueue;
  if (explicitTicket !== undefined) {
    return {
      ticket: explicitTicket,
      explicitQueue: remainingExplicit,
      pendingUser: args.pendingUser,
    };
  }

  return {
    ticket: args.pendingUser,
    explicitQueue: [],
    pendingUser: null,
  };
}

export function expirePendingRealtimeSpeechTurn<
  T extends { speechTurnId: number },
>(pending: T | null, speechTurnId: number): T | null {
  return pending?.speechTurnId === speechTurnId ? null : pending;
}

/**
 * Hang-up ends the lease before the usage drain, so a tab closed right after
 * hang-up still ends the call server-side (#3925). Final usage still bills:
 * the server admits it for REALTIME_USAGE_POST_TERMINAL_GRACE_MS after the end.
 */
export async function finishRealtimeCallGracefully(args: {
  stopInput: () => void;
  endLease: () => void;
  drainUsage: () => Promise<void>;
  invalidate: () => void;
  isCurrent?: () => boolean;
  timeoutMs: number;
}): Promise<void> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    try {
      args.stopInput();
    } finally {
      args.endLease();
    }
    const timeoutPromise = new Promise<void>((resolve) => {
      timeout = setTimeout(resolve, args.timeoutMs);
    });
    await Promise.race([args.drainUsage(), timeoutPromise]);
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
    if (args.isCurrent?.() ?? true) args.invalidate();
  }
}

export function stopRealtimeVoiceMediaImmediately(args: {
  stopTurns: () => Promise<void>;
  stopMedia: () => void;
}): Promise<void> {
  try {
    return args.stopTurns();
  } finally {
    args.stopMedia();
  }
}

export function isUnexpectedRealtimeDisconnect(
  connectionStatus: 'connecting' | 'connected' | 'disconnected',
  wasConnected: boolean
): boolean {
  return connectionStatus === 'disconnected' && wasConnected;
}

export async function withRealtimeConnectTimeout<T>(
  connection: Promise<T>,
  options: { timeoutMs: number; onTimeout: () => void }
): Promise<T> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      options.onTimeout();
      reject(new Error('Voice connection timed out'));
    }, options.timeoutMs);
  });

  try {
    return await Promise.race([connection, timeoutPromise]);
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

export function getRealtimeLeaseExpiryDelayMs(args: {
  leaseDurationMs: number;
}): number {
  return Math.max(0, args.leaseDurationMs);
}

export function scheduleRealtimeLeaseExpiry(args: {
  leaseDurationMs: number;
  onExpiry: () => void;
}): () => void {
  const timer = setTimeout(
    args.onExpiry,
    getRealtimeLeaseExpiryDelayMs({
      leaseDurationMs: args.leaseDurationMs,
    })
  );
  return () => clearTimeout(timer);
}

function defaultSleep(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

export async function retryRealtimeUsageReport(
  report: () => Promise<unknown>,
  options?: {
    delaysMs?: readonly number[];
    sleep?: (delayMs: number) => Promise<void>;
  }
): Promise<void> {
  const delaysMs = options?.delaysMs ?? DEFAULT_USAGE_RETRY_DELAYS_MS;
  const sleep = options?.sleep ?? defaultSleep;

  for (let attempt = 0; ; attempt += 1) {
    try {
      await report();
      return;
    } catch (error) {
      const delayMs = delaysMs[attempt];
      if (delayMs === undefined) throw error;
      await sleep(delayMs);
    }
  }
}

export async function uploadRealtimeVoiceRecordingBlob(args: {
  uploadUrl: string;
  contentType: string;
  blob: Blob;
  timeoutMs?: number;
}): Promise<Response> {
  const timeoutMs = args.timeoutMs ?? DEFAULT_RECORDING_UPLOAD_TIMEOUT_MS;
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await uploadViaProxy(args.uploadUrl, args.blob, args.contentType, {
      timeoutMs,
      signal: controller.signal,
    });
  } catch (error) {
    if (timedOut) {
      throw new Error(
        `Voice recording upload timed out after ${Math.round(timeoutMs / 1000)}s`
      );
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}
import { uploadViaProxy } from '@/lib/uploads/upload-via-proxy';
